import { useEffect } from "react";
import { useParams } from "react-router-dom";
import BlogNavbar from "@/components/feature/BlogNavbar";
import Footer from "@/components/feature/Footer";
import { seoArticles } from "@/mocks/articles";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mulebuyspreadsheet.my";

function buildArticleJsonLd(article: { title: string; slug: string; description: string; image: string; readTime: string }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.description,
        "image": article.image,
        "datePublished": "2026-06-22",
        "dateModified": "2026-06-22",
        "author": {
          "@type": "Organization",
          "name": "Mulebuy Spreadsheet",
          "url": siteUrl
        },
        "publisher": {
          "@type": "Organization",
          "name": "Mulebuy Spreadsheet",
          "url": siteUrl
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}/blog/${article.slug}`
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": `${siteUrl}/blog/what-is-mulebuy-spreadsheet`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title
          }
        ]
      }
    ]
  };
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <BlogNavbar />
        <main className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
          <a href="/" className="px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap inline-flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            <span>Back to Home</span>
          </a>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <BlogNavbar />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <nav className="text-sm text-foreground-500 mb-6">
          <a href="/" className="hover:text-foreground-800 transition-colors">Home</a>
          <span className="mx-2">/</span>
          <span className="text-foreground-700">Blog</span>
          <span className="mx-2">/</span>
          <span className="text-foreground-800">{article.title}</span>
        </nav>

        <article>
          <header className="mb-10 md:mb-12">
            <span className="text-primary-600 text-xs font-semibold tracking-wide uppercase mb-3 block">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {article.description}
            </p>
            <div className="flex items-center gap-4 mt-6 text-foreground-500 text-xs">
              <span>Updated June 22, 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime}</span>
            </div>
          </header>

          {article.image && (
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          )}

          <div className="prose-custom">
            {article.sections.map((section, idx) => {
              if (section.type === "heading") {
                return (
                  <h2 key={idx} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 mt-10">
                    {section.heading}
                  </h2>
                );
              }

              if (section.type === "paragraph") {
                return (
                  <p
                    key={idx}
                    className="text-foreground-700 text-sm md:text-base leading-relaxed mb-5"
                    dangerouslySetInnerHTML={{ __html: section.text || "" }}
                  />
                );
              }

              if (section.type === "list" && section.items) {
                return (
                  <div key={idx} className="my-6">
                    {section.heading && (
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">{section.heading}</h3>
                    )}
                    <ul className="space-y-3">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                          <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                            <i className="ri-check-line text-primary-500"></i>
                          </div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }

              if (section.type === "table" && section.tableData) {
                return (
                  <div key={idx} className="my-8 md:my-10">
                    {section.heading && (
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">{section.heading}</h3>
                    )}
                    <div className="overflow-hidden rounded-xl border border-background-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-foreground-950 text-white">
                            {section.tableData.headers.map((h, i) => (
                              <th key={i} className="text-left px-4 py-3 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.tableData.rows.map((row, ri) => (
                            <tr key={ri} className={ri % 2 === 0 ? "bg-background-50" : "bg-background-100"}>
                              {row.map((cell, ci) => (
                                <td key={ci} className={`px-4 py-3 ${ci === 0 ? "text-foreground-800 font-medium" : ci >= row.length - 2 ? "text-primary-700 font-medium" : "text-foreground-600"}`}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }

              if (section.type === "faq" && section.faqs) {
                return (
                  <div key={idx} className="bg-primary-50/60 rounded-2xl border border-primary-200/50 p-6 md:p-8 my-10 md:my-12">
                    {section.heading && (
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">{section.heading}</h2>
                    )}
                    <div className="space-y-6">
                      {section.faqs.map((faq, fi) => (
                        <div key={fi}>
                          <h3 className="font-heading font-bold text-foreground-950 text-sm md:text-base mb-2">
                            {faq.question}
                          </h3>
                          <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (section.type === "cta") {
                return (
                  <div key={idx} className="bg-foreground-950 rounded-2xl p-6 md:p-8 text-center my-10 md:my-12">
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                      {section.text}
                    </h3>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 max-w-lg mx-auto">
                      Visit our main resource platform to access the complete product catalog and start sourcing smarter today.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-8 py-3.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-2"
                      >
                        <span>Buy It Now</span>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </div>
                      </a>
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-8 py-3.5 bg-white/10 text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/20 transition-colors whitespace-nowrap"
                      >
                        Mulebuy Spreadsheet
                      </a>
                    </div>
                  </div>
                );
              }

              return null;
            })}

            <div className="mt-12 pt-8 border-t border-background-200/50">
              <p className="text-foreground-600 text-sm">
                <a href="/" className="text-primary-600 font-medium hover:underline">← Back to Home</a>
                {" "}| Explore more{" "}
                <a href="/blog/what-is-mulebuy-spreadsheet" className="text-primary-600 font-medium hover:underline">mulebuy spreadsheet</a> guides and resources.
              </p>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <JsonLd data={buildArticleJsonLd(article)} />
    </div>
  );
}